import type { AgentItem, AttentionItem } from './types';
import type { FilterAxis } from './multiAxisFiltering';

export function sortRecentAttentionItems(items: AttentionItem[]): AttentionItem[] {
  return [...items].sort((left, right) => {
    const leftTime = left.updatedAt ? Date.parse(left.updatedAt) : 0;
    const rightTime = right.updatedAt ? Date.parse(right.updatedAt) : 0;
    return rightTime - leftTime;
  });
}

/**
 * Single source of truth for Jira/GitHub/Slack/Agents filter axes, on the
 * shared multi-axis filtering engine (`domain/multiAxisFiltering.ts`).
 * Every provider exposes its options through the shared `FilterButton`
 * (`ui/common/FilterButton.tsx`) rather than an always-visible pill row.
 * The attention-radar feed defines its own axes (`RADAR_AXES` in
 * `domain/radarThreads.ts`) on the same engine.
 */

export const JIRA_AXES: FilterAxis<AttentionItem>[] = [
  {
    id: 'status',
    label: 'Estado',
    type: 'multi',
    options: [
      { value: 'you', label: 'Te esperan a ti', predicate: (i) => i.st === 'you' },
      {
        value: 'review',
        label: 'En revisión',
        predicate: (i) => ['Pull request', 'Ready for QA', 'In QA'].includes(i.workflow || ''),
      },
      { value: 'blocked', label: 'Bloqueados', predicate: (i) => i.st === 'blocked' },
    ],
  },
];

export const GITHUB_AXES: FilterAxis<AttentionItem>[] = [
  {
    id: 'status',
    label: 'Estado',
    type: 'multi',
    options: [
      { value: 'reviewer', label: 'Te piden revisión', predicate: (i) => i.role === 'reviewer' },
      {
        value: 'changes-requested',
        label: 'Cambios solicitados',
        predicate: (i) => i.role === 'author' && i.st === 'you',
      },
      { value: 'blocked', label: 'CI en rojo', predicate: (i) => i.st === 'blocked' },
      { value: 'ready', label: 'Listo para mergear', predicate: (i) => i.st === 'ready' },
      { value: 'author', label: 'Mis PRs', predicate: (i) => i.role === 'author' },
    ],
  },
];

export const SLACK_AXES: FilterAxis<AttentionItem>[] = [
  {
    id: 'tags',
    label: 'Etiquetas',
    type: 'multi',
    options: [
      { value: 'atencion', label: 'Atención', predicate: (i) => !!i.tags?.includes('Atención') },
      { value: 'menciones', label: 'Menciones', predicate: (i) => !!i.tags?.includes('Menciones') },
      {
        value: 'te-nombraron',
        label: 'Te nombraron',
        predicate: (i) => !!i.tags?.includes('Te nombraron'),
      },
      {
        value: 'tu-ambito',
        label: 'Tu ámbito',
        predicate: (i) => !!i.tags?.includes('Tu ámbito'),
      },
      {
        value: 'decisiones',
        label: 'Decisiones',
        predicate: (i) => !!i.tags?.includes('Decisiones'),
      },
      {
        value: 'seguimiento',
        label: 'Seguimiento',
        predicate: (i) => !!i.tags?.includes('Seguimiento'),
      },
    ],
  },
];

export const GMAIL_AXES: FilterAxis<AttentionItem>[] = [
  {
    id: 'status',
    label: 'Estado',
    type: 'multi',
    options: [
      { value: 'unread', label: 'No leídos', predicate: (i) => i.unread === true },
      { value: 'blocks-work', label: 'Bloquean trabajo', predicate: (i) => i.blocksWork === true },
      { value: 'externo', label: 'Externos', predicate: (i) => i.kind === 'Externo' },
      { value: 'interno', label: 'Internos', predicate: (i) => i.kind === 'Interno' },
      { value: 'aviso', label: 'Avisos', predicate: (i) => i.kind === 'Aviso' },
    ],
  },
];

export const AGENT_AXES: FilterAxis<AgentItem>[] = [
  {
    id: 'status',
    label: 'Estado',
    type: 'multi',
    options: [
      { value: 'running', label: 'En curso', predicate: (agent) => agent.running === true },
      {
        value: 'needs-intervention',
        label: 'Necesita intervención',
        predicate: (agent) => agent.running === false,
      },
      {
        value: 'duplicates',
        label: 'Duplicados',
        predicate: (agent) => agent.ticket === 'DEV-3056',
      },
    ],
  },
];
